// TipTap ベースの WYSIWYG エディタ (Phase D)
// - 太字 / 斜体 / 下線 / 打消し / 見出し / 箇条書き / リンク / 画像 / 文字色
// - 画像挿入 + 25/50/75/100% リサイズ
// - YouTube / Vimeo URL 貼り付けで iframe 埋め込み
// - HTML ソース編集モード (split view で live preview)
// - ブラウザ popup (prompt/confirm/alert) は使わない
import { useCallback, useRef, useState, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Node } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { uploadImage, uploadsUrl } from "../lib/uploader";
import { RichTextRender } from "./RichTextRender";
import { isKnownVideoUrl, toVideoEmbedUrl } from "../../shared/video-embed";

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

const COLOR_SWATCHES = [
  { label: "黒", value: "#111111" },
  { label: "赤", value: "#dc2626" },
  { label: "青", value: "#2563eb" },
  { label: "緑", value: "#059669" },
  { label: "橙", value: "#d97706" },
  { label: "灰", value: "#6b7280" },
];

// 画像リサイズ対応 Image
const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute("width"),
        renderHTML: (attrs: { width?: string | null }) =>
          attrs.width ? { width: attrs.width } : {},
      },
    };
  },
});

// YouTube / Vimeo 埋め込み用 iframe node
// insertContent("<iframe ...>") で TipTap が iframe を保持できるようにする
const Iframe = Node.create({
  name: "iframe",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      src: { default: null },
      frameborder: { default: "0" },
      allow: { default: null },
      allowfullscreen: {
        default: true,
        parseHTML: () => true,
        renderHTML: () => ({ allowfullscreen: "" }),
      },
      width: { default: "100%" },
      height: { default: "360" },
    };
  },
  parseHTML() {
    return [{ tag: "iframe" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["iframe", HTMLAttributes];
  },
});

// URL から YouTube / Vimeo の iframe HTML を生成。許容外は null。
// 埋込 URL の組み立ては shared/video-embed.ts を正本とする
// (限定公開 Vimeo の ?h= ハッシュ保持もそこで面倒を見る)。
function videoEmbedFromUrl(url: string): string | null {
  if (!isKnownVideoUrl(url)) return null;
  const src = toVideoEmbedUrl(url);
  if (!src) return null;
  const allow = src.includes("youtube.com")
    ? "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    : "autoplay; fullscreen; picture-in-picture";
  return `<iframe src="${src}" width="100%" height="360" frameborder="0" allow="${allow}" allowfullscreen></iframe>`;
}

export function RichTextEditor({ value, onChange, placeholder }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoErr, setVideoErr] = useState<string | null>(null);
  // source mode: HTML textarea + live preview
  const [sourceMode, setSourceMode] = useState(false);
  const [sourceHtml, setSourceHtml] = useState(value || "");
  const [, setTick] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
        },
      }),
      ResizableImage.configure({ inline: false, allowBase64: false }),
      TextStyle,
      Color,
      Iframe,
    ],
    content: value || "",
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[200px] p-3 outline-none focus:ring-1 focus:ring-tora rounded",
      },
      // ペースト時に Vimeo/YouTube embed コードや URL を自動検知して iframe 化
      handlePaste: (view, event) => {
        const text = event.clipboardData?.getData("text/plain") ?? "";
        const html = event.clipboardData?.getData("text/html") ?? "";
        const content = html || text;

        // embed コード (iframe src) を検出
        const iframeSrc = content.match(
          /src=["']?(https:\/\/player\.vimeo\.com\/video\/\d+[^"'\s>]*|https:\/\/(?:www\.)?youtube\.com\/embed\/[A-Za-z0-9_-]+[^"'\s>]*)/,
        );
        if (iframeSrc) {
          const src = iframeSrc[1].replace(/&amp;/g, "&");
          view.dispatch(view.state.tr);
          setTimeout(() => {
            editor?.chain().focus().insertContent(
              `<iframe src="${src}" width="100%" height="360" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`,
            ).run();
          }, 0);
          return true;
        }

        // プレーン URL (vimeo.com/xxx or youtube) を検出
        const urlOnly = text.trim();
        if (/^https?:\/\//.test(urlOnly) && !html) {
          const embed = videoEmbedFromUrl(urlOnly);
          if (embed) {
            view.dispatch(view.state.tr);
            setTimeout(() => {
              editor?.chain().focus().insertContent(embed).run();
            }, 0);
            return true;
          }
        }

        return false; // 通常のペースト
      },
    },
  });

  // 親から渡される value が外部要因 (編集ボタンで別レコード読込など) で
  // 変化したらエディタ内容を同期する。useEditor の content はマウント時のみ
  // 反映されるため、これが無いと編集時に本文が空のままになる。
  // ユーザーのタイプ中 (editor.getHTML() === value) は setContent しない
  // (カーソル位置リセットを防ぐ)。
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (next !== current) {
      // フォーカス中の自分のタイプによる差分でない場合のみ同期
      if (!editor.isFocused) {
        editor.commands.setContent(next);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    const rerender = () => setTick((t) => t + 1);
    editor.on("selectionUpdate", rerender);
    editor.on("transaction", rerender);
    editor.on("focus", rerender);
    editor.on("blur", rerender);
    return () => {
      editor.off("selectionUpdate", rerender);
      editor.off("transaction", rerender);
      editor.off("focus", rerender);
      editor.off("blur", rerender);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor || !linkOpen) return;
    const prev = editor.getAttributes("link").href as string | undefined;
    setLinkUrl(prev ?? "");
  }, [linkOpen, editor]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url, target: "_blank" })
        .run();
    }
    setLinkOpen(false);
  }, [editor, linkUrl]);

  const clearLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setLinkOpen(false);
  }, [editor]);

  const applyVideo = useCallback(() => {
    if (!editor) return;
    const url = videoUrl.trim();
    if (!url) {
      setVideoErr("URL を入力してください");
      return;
    }
    const html = videoEmbedFromUrl(url);
    if (!html) {
      setVideoErr(
        "YouTube または Vimeo の URL を入れてください (例: https://www.youtube.com/watch?v=... / https://vimeo.com/...)",
      );
      return;
    }
    editor.chain().focus().insertContent(html).run();
    setVideoUrl("");
    setVideoErr(null);
    setVideoOpen(false);
  }, [editor, videoUrl]);

  const insertImage = useCallback(
    async (file: File) => {
      if (!editor) return;
      try {
        const r = await uploadImage(file);
        const url = uploadsUrl(r.fileKey);
        if (!url) return;
        editor.chain().focus().setImage({ src: url, alt: "" }).run();
      } catch (e) {
        onChange(editor.getHTML());
        console.error("image upload failed:", e);
      }
    },
    [editor, onChange],
  );

  const resizeImage = useCallback(
    (widthPercent: number | null) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .updateAttributes("image", {
          width: widthPercent ? `${widthPercent}%` : null,
        })
        .run();
    },
    [editor],
  );

  const setColor = useCallback(
    (color: string | null) => {
      if (!editor) return;
      if (color === null) editor.chain().focus().unsetColor().run();
      else editor.chain().focus().setColor(color).run();
    },
    [editor],
  );

  // source mode toggle
  const openSourceMode = useCallback(() => {
    if (!editor) return;
    setSourceHtml(editor.getHTML());
    setSourceMode(true);
  }, [editor]);
  const applySource = useCallback(() => {
    if (!editor) return;
    editor.commands.setContent(sourceHtml);
    onChange(sourceHtml);
    setSourceMode(false);
  }, [editor, sourceHtml, onChange]);
  const cancelSource = useCallback(() => {
    if (!editor) return;
    setSourceHtml(editor.getHTML());
    setSourceMode(false);
  }, [editor]);

  if (!editor) {
    return (
      <div className="input min-h-[200px] text-sm text-tora-ink/60">
        エディタを準備中…
      </div>
    );
  }

  const isImageActive = editor.isActive("image");

  const btn = (active: boolean, disabled = false) =>
    `px-2 py-1 text-xs rounded border transition-colors ${
      disabled
        ? "bg-black/5 text-tora-ink/40 border-black/10 cursor-not-allowed"
        : active
          ? "bg-tora text-white border-tora"
          : "bg-white text-tora-ink border-black/15 hover:bg-black/5"
    }`;

  return (
    <div className="rounded border border-black/15 bg-white">
      <div className="flex flex-wrap gap-1 p-2 border-b border-black/10">
        <button
          type="button"
          className={btn(editor.isActive("bold"))}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="太字"
          disabled={sourceMode}
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className={btn(editor.isActive("italic"))}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="斜体"
          disabled={sourceMode}
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className={btn(editor.isActive("underline"))}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="下線"
          disabled={sourceMode}
        >
          <span className="underline">U</span>
        </button>
        <button
          type="button"
          className={btn(editor.isActive("strike"))}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          title="打消し"
          disabled={sourceMode}
        >
          <span className="line-through">S</span>
        </button>
        <span className="mx-1 w-px bg-black/10" />
        <button
          type="button"
          className={btn(editor.isActive("heading", { level: 2 }))}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          title="見出し (大)"
          disabled={sourceMode}
        >
          H2
        </button>
        <button
          type="button"
          className={btn(editor.isActive("heading", { level: 3 }))}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          title="見出し (小)"
          disabled={sourceMode}
        >
          H3
        </button>
        <span className="mx-1 w-px bg-black/10" />
        <button
          type="button"
          className={btn(editor.isActive("bulletList"))}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="箇条書き"
          disabled={sourceMode}
        >
          ・
        </button>
        <button
          type="button"
          className={btn(editor.isActive("orderedList"))}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="番号付きリスト"
          disabled={sourceMode}
        >
          1.
        </button>
        <button
          type="button"
          className={btn(editor.isActive("blockquote"))}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          title="引用"
          disabled={sourceMode}
        >
          ❝
        </button>
        <span className="mx-1 w-px bg-black/10" />
        <button
          type="button"
          className={btn(editor.isActive("link") || linkOpen)}
          onClick={() => {
            setVideoOpen(false);
            setLinkOpen((v) => !v);
          }}
          title="リンク (選択した文字列に URL を付与)"
          disabled={sourceMode}
        >
          🔗
        </button>
        <button
          type="button"
          className={btn(false)}
          onClick={() => fileInputRef.current?.click()}
          title="画像を挿入"
          disabled={sourceMode}
        >
          🖼
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void insertImage(f);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
        />
        <button
          type="button"
          className={`${btn(videoOpen)} !px-2 flex items-center gap-1`}
          onClick={() => {
            setLinkOpen(false);
            setVideoOpen((v) => !v);
          }}
          title="YouTube / Vimeo の動画を埋め込む"
          disabled={sourceMode}
        >
          🎬 <span className="text-[10px]">動画</span>
        </button>
        <span className="mx-1 w-px bg-black/10" />
        {COLOR_SWATCHES.map((c) => (
          <button
            key={c.value}
            type="button"
            className="w-5 h-5 rounded border border-black/20 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: c.value }}
            title={`文字色: ${c.label}`}
            onClick={() => setColor(c.value)}
            disabled={sourceMode}
          />
        ))}
        <button
          type="button"
          className={btn(false)}
          onClick={() => setColor(null)}
          title="文字色をリセット"
          disabled={sourceMode}
        >
          色解除
        </button>
        <span className="ml-auto" />
        <button
          type="button"
          className={btn(sourceMode)}
          onClick={sourceMode ? cancelSource : openSourceMode}
          title="HTML ソースモード"
        >
          {"</>"}
        </button>
      </div>

      {/* リンク入力パネル */}
      {linkOpen && !sourceMode && (
        <div className="flex flex-wrap items-center gap-2 p-2 border-b border-black/10 bg-black/5">
          <label className="text-xs text-tora-ink/70">リンク URL:</label>
          <input
            type="url"
            className="input flex-1 min-w-[240px]"
            placeholder="https://..."
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setLinkOpen(false);
              }
            }}
            autoFocus
          />
          <button type="button" className="btn-primary text-xs" onClick={applyLink}>
            適用
          </button>
          <button type="button" className="btn-outline text-xs" onClick={clearLink}>
            解除
          </button>
          <button
            type="button"
            className="text-xs underline text-tora-ink/60"
            onClick={() => setLinkOpen(false)}
          >
            閉じる
          </button>
          <p className="w-full text-[11px] text-tora-ink/50">
            あらかじめ本文内で文字を範囲選択してから「適用」すると、その文字がリンクになります。
          </p>
        </div>
      )}

      {/* 動画埋め込みパネル */}
      {videoOpen && !sourceMode && (
        <div className="flex flex-wrap items-center gap-2 p-2 border-b border-black/10 bg-black/5">
          <label className="text-xs text-tora-ink/70">動画 URL:</label>
          <input
            type="url"
            className="input flex-1 min-w-[280px]"
            placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
            value={videoUrl}
            onChange={(e) => {
              setVideoUrl(e.target.value);
              setVideoErr(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyVideo();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setVideoOpen(false);
              }
            }}
            autoFocus
          />
          <button type="button" className="btn-primary text-xs" onClick={applyVideo}>
            挿入
          </button>
          <button
            type="button"
            className="text-xs underline text-tora-ink/60"
            onClick={() => setVideoOpen(false)}
          >
            閉じる
          </button>
          {videoErr && (
            <p className="w-full text-[11px] text-red-600">{videoErr}</p>
          )}
          <p className="w-full text-[11px] text-tora-ink/50">
            対応: YouTube (watch / shorts / embed / youtu.be) と Vimeo のみ。
            他ホストの iframe はセキュリティのため挿入できません。
            限定公開 Vimeo は <code>https://vimeo.com/数字/英数字</code> の形のまま貼り付けてください
            (末尾の英数字を消すと再生できません)。
          </p>
        </div>
      )}

      {/* 画像サイズ調整パネル */}
      {!sourceMode && (
        <div className="flex flex-wrap items-center gap-1 p-2 border-b border-black/10 text-xs">
          <span className="text-tora-ink/60 mr-1">画像サイズ:</span>
          {[25, 50, 75, 100].map((p) => (
            <button
              key={p}
              type="button"
              className={btn(false, !isImageActive)}
              disabled={!isImageActive}
              onClick={() => resizeImage(p)}
              title={`選択中の画像を ${p}% に`}
            >
              {p}%
            </button>
          ))}
          <button
            type="button"
            className={btn(false, !isImageActive)}
            disabled={!isImageActive}
            onClick={() => resizeImage(null)}
            title="原寸 (属性解除)"
          >
            原寸
          </button>
          <span className="text-[11px] text-tora-ink/50 ml-2">
            {isImageActive
              ? "画像選択中"
              : "(本文内の画像をクリックしてサイズ変更)"}
          </span>
        </div>
      )}

      {/* body: WYSIWYG or source mode (split view) */}
      {!sourceMode ? (
        <>
          <EditorContent editor={editor} />
          {placeholder && !editor.getText().length && (
            <p className="px-3 pb-2 text-xs text-tora-ink/40">{placeholder}</p>
          )}
        </>
      ) : (
        <div className="p-2 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <p className="text-xs text-tora-ink/60 mb-1">
                HTML ソース (編集可)
              </p>
              <textarea
                className="input min-h-[240px] font-mono text-xs"
                value={sourceHtml}
                onChange={(e) => setSourceHtml(e.target.value)}
              />
            </div>
            <div>
              <p className="text-xs text-tora-ink/60 mb-1">プレビュー (sanitize 後)</p>
              <div className="border border-black/10 rounded p-3 min-h-[240px] bg-white overflow-auto">
                <RichTextRender html={sourceHtml} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="btn-primary text-xs" onClick={applySource}>
              反映してエディタへ戻る
            </button>
            <button type="button" className="btn-outline text-xs" onClick={cancelSource}>
              破棄してエディタへ戻る
            </button>
            <p className="text-[11px] text-tora-ink/50">
              YouTube / Vimeo 以外の iframe や &lt;script&gt; はプレビュー時に削除されます。
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
